import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useMotionValue,
  useMotionTemplate,
  useScroll as useFramerScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { Reveal, SectionNo, Hairline, MaskReveal } from "@/components/primitives";
import BigSectionNo from "@/components/BigSectionNo";
import FilmText from "@/components/FilmText";
import { useCapabilities } from "@/lib/device";
import useIosSafeVideo from "@/lib/useIosSafeVideo";
import { stills, films, pairs } from "@/config/media";

const WipeStage = lazy(() => import("@/components/three/WipeStage"));

function useTimecode(running) {
  const [tc, setTc] = useState("00:00:00:00");
  useEffect(() => {
    if (!running) return;
    let raf;
    const start = performance.now();
    let last = -1;
    const loop = (now) => {
      const f = Math.floor(((now - start) / 1000) * 24);
      if (f !== last) {
        last = f;
        const fr = f % 24;
        const s = Math.floor(f / 24) % 60;
        const m = Math.floor(f / 1440) % 60;
        setTc(
          `00:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}:${String(fr).padStart(2, "0")}`,
        );
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [running]);
  return tc;
}

// DOM path: the real film plays underneath; the still is clipped on top.
function StillOverlay({ progress, still, handleLeft }) {
  const rightInset = useTransform(progress, (p) => (1 - p) * 100);
  const clip = useMotionTemplate`inset(0 ${rightInset}% 0 0)`;
  return (
    <>
      <motion.div className="absolute inset-0 z-10" style={{ clipPath: clip }}>
        <img
          src={`${still.base}-1600.jpg`}
          alt={still.alt}
          loading="lazy"
          className="h-full w-full object-cover"
          style={{ filter: "saturate(0.5) brightness(0.88)" }}
        />
      </motion.div>
      <motion.div
        className="absolute inset-y-0 z-10 w-px bg-gold/80"
        style={{ left: handleLeft }}
        aria-hidden="true"
      />
    </>
  );
}

export default function BeforeAfter() {
  const caps = useCapabilities();
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const dragging = useRef(false);
  const inView = useInView(frameRef, { margin: "300px 0px" });
  const playing = useInView(frameRef, { margin: "-10% 0px" });
  const tc = useTimecode(playing);
  const [pairIdx, setPairIdx] = useState(0);
  const [videoEl, setVideoEl] = useState(null);
  const videoRef = useRef(null);
  const setVideoRefs = (el) => {
    setVideoEl(el);
    videoRef.current = el;
  };
  useIosSafeVideo(videoRef);
  const [glMounted, setGlMounted] = useState(false);

  const pair = pairs[pairIdx];
  const still = stills[pair.still];
  const useGl = caps.ready && caps.webgl;

  useEffect(() => {
    if (useGl && inView) setGlMounted(true);
  }, [useGl, inView]);

  // the film plays only while the frame is near the viewport
  useEffect(() => {
    if (!videoEl) return;
    if (playing && !caps.reduced) videoEl.play().catch(() => {});
    else videoEl.pause();
  }, [videoEl, playing, caps.reduced, pairIdx]);

  const progress = useMotionValue(0.5);
  const handleLeft = useTransform(progress, (p) => `${p * 100}%`);

  const { scrollYProgress: wipeScroll } = useFramerScroll({
    target: sectionRef,
    offset: ["start 75%", "end 45%"],
  });
  useEffect(
    () =>
      wipeScroll.on("change", (v) => {
        if (!dragging.current) progress.set(0.18 + v * 0.64);
      }),
    [wipeScroll, progress],
  );

  const { scrollYProgress: enterScroll } = useFramerScroll({
    target: sectionRef,
    offset: ["start 95%", "start 35%"],
  });
  const barScale = useSpring(enterScroll, { stiffness: 84, damping: 24 });

  useEffect(() => {
    const onResize = () =>
      progress.set(Math.min(0.94, Math.max(0.06, progress.get())));
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [progress]);

  const setFromClientX = (clientX) => {
    const r = frameRef.current?.getBoundingClientRect();
    if (!r) return;
    progress.set(Math.min(0.94, Math.max(0.06, (clientX - r.left) / r.width)));
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => dragging.current && setFromClientX(e.clientX);
  const endDrag = () => {
    dragging.current = false;
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      dragging.current = true;
      const delta = e.key === "ArrowLeft" ? -0.06 : 0.06;
      progress.set(Math.min(0.94, Math.max(0.06, progress.get() + delta)));
    }
  };

  return (
    <section
      id="living-frame"
      ref={sectionRef}
      className="relative overflow-hidden px-6 py-20 sm:px-10 md:py-28 warm-radial-r"
      data-testid="before-after"
    >
      <BigSectionNo n="02" />
      <div className="mx-auto max-w-[1280px]">
        <Hairline className="mb-20" />
        <div className="mx-auto flex max-w-[62ch] flex-col items-center text-center">
          <Reveal>
            <SectionNo n="02" label="Drag the handle" />
          </Reveal>
          <h2
            className="display mt-8 text-primaryText"
            style={{
              fontSize: "clamp(2.25rem, 5vw, 4rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.015em",
              fontWeight: 300,
            }}
          >
            <MaskReveal delay={0.05}>One photograph.</MaskReveal>
            <MaskReveal delay={0.16}>
              <FilmText texture="T01">Two futures.</FilmText>
            </MaskReveal>
          </h2>
          <Reveal delay={0.18}>
            <p className="mt-8 text-base leading-relaxed text-mutedText sm:text-lg">
              Left, the library image as it exists today. Right, the same
              photograph with depth, atmosphere and movement. No new shoot.
              Same picture. Each film here was made from the still beside it,
              and from <em>nothing else</em>.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div
            ref={frameRef}
            className="on-dark relative mt-16 aspect-video touch-pan-y select-none overflow-hidden rounded-[2px] border border-brass/40 bg-ink"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            data-testid="living-frame"
          >
            <video
              key={pair.id}
              ref={setVideoRefs}
              src={films[pair.film]}
              poster={inView ? `${still.base}-1600.jpg` : undefined}
              muted
              autoPlay
              loop
              playsInline
              preload={inView ? "auto" : "none"}
              className="absolute inset-0 z-0 h-full w-full object-cover"
              aria-label={still.alt}
              data-testid="living-frame-video"
            />
            {glMounted && videoEl && !caps.reduced ? (
              <Suspense
                fallback={
                  <StillOverlay progress={progress} still={still} handleLeft={handleLeft} />
                }
              >
                <WipeStage
                  key={pair.id}
                  stillUrl={`${still.base}-1600.jpg`}
                  video={videoEl}
                  progress={progress}
                  active={inView}
                />
              </Suspense>
            ) : (
              <StillOverlay progress={progress} still={still} handleLeft={handleLeft} />
            )}

            <motion.div
              className="absolute inset-x-0 top-0 z-20 h-[9%] origin-top bg-ink"
              style={{ scaleY: barScale }}
              aria-hidden="true"
            />
            <motion.div
              className="absolute inset-x-0 bottom-0 z-20 h-[9%] origin-bottom bg-ink"
              style={{ scaleY: barScale }}
              aria-hidden="true"
            />

            <motion.div
              className="absolute inset-y-0 z-30 -ml-5 w-10"
              style={{ left: handleLeft }}
              role="slider"
              tabIndex={0}
              aria-label="Reveal the still or the living frame"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress.get() * 100)}
              onKeyDown={onKeyDown}
              data-testid="wipe-handle"
              data-cursor="hover"
            >
              <div className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-gold/70 bg-ink/70 backdrop-blur-sm">
                <span className="font-mono text-[10px] tracking-widest text-gold">
                  ⟷
                </span>
              </div>
            </motion.div>

            <div className="pointer-events-none absolute inset-x-0 top-[9%] z-20 flex items-start justify-between p-5">
              <span className="font-mono text-[10px] tracking-[0.28em] text-mutedText" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.85)" }}>
                THE STILL YOU OWN
              </span>
              <span className="font-mono text-[11px] tracking-widest text-gold" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.85)" }} data-testid="timecode">
                {tc}
              </span>
            </div>
            <div className="pointer-events-none absolute inset-x-0 bottom-[9%] z-20 flex items-end justify-between p-5">
              <span className="font-mono text-[10px] tracking-[0.28em] text-mutedText" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.85)" }}>
                DRAG THE HANDLE
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] text-gold" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.85)" }}>
                <motion.span
                  className="h-1.5 w-1.5 rounded-full bg-gold"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                />
                THE FRAME WE RETURN · 24 FPS
              </span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div
            className="mt-10 flex flex-wrap items-center justify-center gap-3"
            data-testid="pair-stepper"
          >
            {pairs.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setPairIdx(i)}
                aria-pressed={pairIdx === i}
                data-testid={`pair-${i}`}
                className={`btn-sweep rounded-full border px-6 py-3 font-mono text-[10px] tracking-[0.24em] transition-all duration-300 ${
                  pairIdx === i
                    ? "border-gold bg-gold text-ink shadow-[0_10px_28px_-12px_rgba(138,111,60,0.55)]"
                    : "border-gold/25 bg-surface/60 text-mutedText hover:border-gold/60 hover:bg-surface hover:text-primaryText"
                }`}
              >
                0{i + 1} · {p.label.toUpperCase()}
              </button>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
